import Button from "@/components/Button";
import Spinner from "@/components/Spinner";
import { StripePriceMetadata } from "@/src/features/pricing/types";
import { formatCurrency } from "@/src/utils/formatCurrency";
import clsx from "clsx";

export interface SimplifiedPrice {
  id: string;
  unit_amount: number | null;
  currency: string;
  recurring?: { interval: string } | null;
  metadata: StripePriceMetadata;
}

// Update props to use the simplified price type
interface SubscriptionCardProps {
  price?: SimplifiedPrice; // Made price optional
  isYearly: boolean;
  hasHadATrial: boolean;
  isLoading?: boolean; // Added isLoading prop
}

export const SubscriptionCard = ({
  price,
  isYearly,
  hasHadATrial,
  isLoading,
}: SubscriptionCardProps) => {
  const metadata = !isLoading && price ? price.metadata : undefined;

  // Derive display details from metadata or show placeholders
  const displayedPriceText = !isLoading && metadata ? metadata.heading : "";
  const crossedPriceText = !isLoading && metadata ? metadata.crossed_price : "";
  const subheadingText =
    !isLoading && metadata
      ? isYearly
        ? metadata.module_subheading
        : metadata.subheading
      : "";

  let buttonLabel = "Loading...";

  if (!isLoading) {
    buttonLabel = hasHadATrial
      ? "Unlock Mob Premium"
      : "Start 5 day free trial";
  }

  return (
    <div
      className={clsx(
        "group relative flex w-full items-start justify-between overflow-hidden rounded-2xl p-4 leading-none transition",
        "bg-white text-zinc-800",
        "rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-200"
      )}
    >
      <div className="flex w-full flex-col items-start justify-between gap-y-1">
        <div className="flex items-center gap-x-2 font-spatial text-xl font-medium sm:text-3xl">
          <div>{isYearly ? "Annual" : "Monthly"}</div>
          {isLoading && <Spinner />}
        </div>
        <div
          className={clsx(
            "h-7", // height is roughly the height of the rendered text
            "rounded-2xl text-left font-spatial text-base font-medium tracking-tighter text-zinc-900 sm:text-lg",
            {
              "w-[200px] bg-zinc-100": isLoading, // width is roughly the width of the rendered text
            }
          )}
        >
          {crossedPriceText && (
            <s className="mr-2 text-grey-500">{crossedPriceText}</s>
          )}
          {isYearly && displayedPriceText?.includes(" / ") ? (
            <>
              <span className="font-normal text-[#70AD82]">
                {displayedPriceText?.split(" / ")[0]}
              </span>{" "}
              <span className="text-zinc-500">
                / {displayedPriceText?.split(" / ")[1]}
              </span>
            </>
          ) : (
            <span>{displayedPriceText}</span>
          )}
        </div>

        <div
          className={clsx(
            "h-[13px]", // height is roughly the height of the rendered text
            "rounded-2xl font-body text-xs font-medium text-zinc-500",
            {
              "w-32 bg-zinc-100": isLoading,
            }
          )}
        >
          {subheadingText}
        </div>

        <div className="mt-3 flex flex-col items-center">
          <Button theme="light" label={buttonLabel} isDisabled={isLoading} />
        </div>
      </div>
      {!hasHadATrial && (
        <div className="flex flex-col items-end text-[#70AD82]">
          <>
            <div className="font-spatial text-xl font-medium">
              {!isLoading && price
                ? formatCurrency(0, price.currency)
                : "£0.00"}
            </div>
            <div className="font-body text-sm font-medium">for 5 days</div>
          </>
        </div>
      )}
    </div>
  );
};
